import React from 'react';
import {
  MapPin, Calendar, Zap, Grid, DollarSign, Clock, FileText, Check, Package
} from 'lucide-react';
import { Fair, Slot, ReservationRentalItemCreate } from '../types';
import { formatCurrency, multiplyCurrency, subtractCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import { formatDateRange, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

interface ReservationSummaryProps {
  slot: Slot;
  fair: Fair;
  reservationType: 'full_duration' | 'per_day';
  selectedDates: string[];
  rentalItems?: ReservationRentalItemCreate[];
  specialRequests: string;
  totalPrice: number;
  discountCode?: string | null; // Issue #375
  discountValid?: boolean; // Issue #375
  discountType?: 'percentage' | 'fixed_amount' | 'free' | null; // Issue #375
  discountValue?: number | null; // Issue #375
  originalPrice?: number; // Issue #375
  onEditDates?: () => void;
  onEditType?: () => void;
  onEditRentals?: () => void;
  showActions?: boolean;
}

export default function ReservationSummary({
  slot,
  fair,
  reservationType,
  selectedDates,
  rentalItems = [],
  specialRequests,
  totalPrice,
  discountCode,
  discountValid = false,
  discountType,
  discountValue,
  originalPrice,
  onEditDates,
  onEditType,
  onEditRentals,
  showActions = true,
}: ReservationSummaryProps) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysCount = () => {
    if (reservationType === 'per_day') {
      return selectedDates.length;
    }
    const start = parseDateOnly(fair.start_date);
    const end = parseDateOnly(fair.end_date);
    if (start && end) {
      return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    }
    return 1;
  };

  const daysCount = getDaysCount();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Reservation Summary</h3>

      {/* Fair Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 mb-3">{fair.name}</h4>
        {fair.address && (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              {fair.address.street}, {fair.address.city}, {fair.address.state} {fair.address.zip}
            </span>
          </div>
        )}
        {fair.start_date && fair.end_date && (
          <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
            <Calendar className="w-4 h-4" />
            <span>
              {formatDateRange(fair.start_date, fair.end_date)}
            </span>
          </div>
        )}
      </div>

      {/* Slot Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Slot #{slot.slot_number}</h4>
          <span className={`px-2 py-1 text-xs rounded-full ${
            slot.slot_type === 'premium' ? 'bg-purple-100 text-purple-700' :
            slot.slot_type === 'preferred' ? 'bg-moonbone text-primary-dark' :
            'bg-gray-100 text-gray-700'
          }`}>
            {slot.slot_type}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {slot.dimensions && (
            <div className="flex items-center gap-2 text-gray-600">
              <Grid className="w-4 h-4" />
              <span>{slot.dimensions}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-gray-600">
            <Zap className={`w-4 h-4 ${slot.has_power ? 'text-yellow-500' : ''}`} />
            <span>{slot.has_power ? 'Power available' : 'No power'}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <DollarSign className="w-4 h-4" />
            <span>{formatCurrency(slot.base_price)}/day</span> {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>
      </div>

      {/* Reservation Type */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900">Reservation Type</h4>
          {showActions && onEditType && (
            <button
              onClick={onEditType}
              className="text-sm text-secondary hover:text-primary-dark"
            >
              Edit
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <span className="text-gray-700">
            {reservationType === 'full_duration' ? 'Full Duration' : 'Per-Day'}
            <span className="text-gray-500 ml-2">
              ({daysCount} {daysCount === 1 ? 'day' : 'days'})
            </span>
          </span>
        </div>
      </div>

      {/* Selected Dates (for per-day) */}
      {reservationType === 'per_day' && selectedDates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Selected Dates</h4>
            {showActions && onEditDates && (
              <button
                onClick={onEditDates}
                className="text-sm text-secondary hover:text-primary-dark"
              >
                Edit
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedDates.sort().map(date => (
              <span
                key={date}
                className="inline-flex items-center gap-1 px-2 py-1 bg-teal-50 text-primary-dark rounded text-sm"
              >
                <Check className="w-3 h-3" />
                {formatDate(date)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Rental Items */}
      {rentalItems && rentalItems.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Rental Equipment
            </h4>
            {showActions && onEditRentals && (
              <button
                onClick={onEditRentals}
                className="text-sm text-secondary hover:text-primary-dark"
              >
                Edit
              </button>
            )}
          </div>
          <div className="space-y-2">
            {rentalItems.map((item, index) => (
              <div key={index} className="flex justify-between items-center text-sm border-b border-gray-100 pb-2 last:border-0">
                <span className="text-gray-700">
                  Item #{item.rental_item_id} × {item.quantity}
                </span>
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-2">
              * Rental equipment costs are included in the total price
            </p>
          </div>
        </div>
      )}

      {/* Special Requests */}
      {specialRequests && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Special Requests
          </h4>
          <p className="text-gray-600 text-sm whitespace-pre-line">{specialRequests}</p>
        </div>
      )}

      {/* Pricing Breakdown */}
      <div className="bg-teal-50 rounded-lg border border-teal-200 p-4">
        <h4 className="font-medium text-gray-900 mb-3">Price Breakdown</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Base price</span>
            <span>{formatCurrency(slot.base_price)}/day</span> {/* Issue #905: Decimal.js formatting */}
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Days</span>
            <span>x {daysCount}</span>
          </div>

          {/* Original Price (if discount applied) - Issue #375 */}
          {discountValid && originalPrice !== undefined && (
            <div className="flex justify-between text-gray-600 pt-2 border-t border-teal-200">
              <span>Subtotal</span>
              <span>{formatCurrency(originalPrice)}</span> {/* Issue #905: Decimal.js formatting */}
            </div>
          )}

          {/* Discount Applied - Issue #375 */}
          {discountValid && discountCode && discountType && discountValue !== null && discountValue !== undefined && originalPrice !== undefined && (
            <div className="flex justify-between text-green-700">
              <span className="flex items-center gap-1">
                <Check className="w-4 h-4" />
                Discount ({discountCode})
              </span>
              <span className="font-medium">
                {/* Issue #905: Decimal.js for discount calculations */}
                {discountType === 'percentage' && `- ${discountValue}% (${formatCurrency(multiplyCurrency(originalPrice, discountValue / 100))})`}
                {discountType === 'fixed_amount' && `- ${formatCurrency(discountValue)}`}
                {discountType === 'free' && `- ${formatCurrency(originalPrice)} (FREE!)`}
              </span>
            </div>
          )}

          <div className="border-t border-teal-200 pt-2 mt-2">
            <div className="flex justify-between font-semibold text-lg">
              <span className="text-gray-900">Total</span>
              <span className={discountValid ? "text-green-600" : "text-secondary"}>
                {formatCurrency(totalPrice)} {/* Issue #905: Decimal.js formatting */}
              </span>
            </div>
            {discountValid && originalPrice !== undefined && originalPrice > totalPrice && (
              <p className="text-xs text-green-600 mt-1 text-right">
                {/* Issue #905: Decimal.js for savings calculation */}
                You save {formatCurrency(subtractCurrency(originalPrice, totalPrice))}!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status Note */}
      <p className="text-sm text-gray-500 text-center">
        Your reservation will be pending until confirmed by the fair manager.
      </p>
    </div>
  );
}
